// The main thread's side of the geometry worker: start, wait for ready, call, recycle — and, since unify-ui ship 2,
// two things every other text tool's client has: a CLOCK on a request and a CHANNEL for a font download's progress.
//
// The clock (ruling R22). call(msg, { limitMs }) answers { ok: false, error: { code: 'timeout' } } when the worker
// has been ON that request for limitMs without an answer. It starts when the WORKER says it has started the request
// (its `started` message, posted after the request's font is in hand) — not when the request is posted: the wait
// for the worker to boot, and a font file on its way (up to 12.8 MB on a phone connection), are not the engine's
// work and are never counted. The engine cannot be interrupted, so a clock that fires only answers the caller;
// ending the worker is the caller's next step: replace().
//
// The channel. A worker that downloads a font posts { type: 'progress', ids, received, total } — `ids` names every
// request waiting on that file; each one's `onProgress` is called with the message.
//
// manifold-3d 3.5.3 leaks ~4KB of wasm heap per evaluated extrude even when every handle is
// deleted (measured 2026-09-21: ~80KB per tag, linear). A wasm heap never shrinks, so the
// worker is replaced after `recycleAfter` meshes/exports, while idle. Layout requests are 2D
// only and do not count.
const MAX_TIMER_MS = 2147483647;    // setTimeout fires at once above this (and for Infinity)
const failed = (code, id) => ({ id, ok: false, error: { code } });

export function createWorkerClient({ url, recycleAfter = 300, onFatal }) {
  let worker = null, ready = null, generations = 0, reqId = 0;
  const pending = new Map();        // id → { resolve, onStarted, onProgress }
  function start() {
    const w = new Worker(url, { type: 'module' });
    generations = 0;
    ready = new Promise(res => { w.onceReady = res; });
    w.onmessage = (ev) => {
      const { type, id } = ev.data;
      if (type === 'ready') { w.onceReady(); return; }
      if (type === 'fatal') { onFatal(ev.data.message); return; }
      if (type === 'progress') { for (const pid of ev.data.ids) pending.get(pid)?.onProgress?.(ev.data); return; }   // one download, many waiters
      if (type === 'started') { pending.get(id)?.onStarted?.(); return; }                                            // the worker is on this request now
      const cb = pending.get(id);
      if (!cb) return;                                             // answered already (its clock fired), or never ours
      pending.delete(id);
      cb.resolve(ev.data);
    };
    worker = w;
  }
  // Always waits for the CURRENT worker to be ready: the worker ignores messages that arrive
  // before its 'ready', so posting early would leave this promise pending forever.
  // limitMs: a number in 1..2147483647 is a clock (see the header); anything else — absent, 0, NaN, Infinity — is none.
  async function call(msg, { limitMs, onProgress } = {}) {
    let w;
    do { w = worker; await ready; } while (w !== worker);          // the worker may be recycled or replaced while we wait
    return new Promise(resolve => {
      const id = ++reqId;                                          // the sequence number the reply echoes
      let timer = null;
      const onStarted = () => { if (limitMs > 0 && limitMs <= MAX_TIMER_MS && timer === null) timer = setTimeout(() => { if (pending.delete(id)) resolve(failed('timeout', id)); }, limitMs); };
      pending.set(id, { resolve: (reply) => { clearTimeout(timer); resolve(reply); }, onStarted, onProgress });
      worker.postMessage({ ...msg, id });
    });
  }
  // A message that wants no answer (the font the visitor's design names now: geometry.worker.js `pick`). Posted to the
  // worker of the moment once it is ready; if that worker is replaced first, the message goes with it.
  function tell(msg) {
    const w = worker;
    Promise.resolve(ready).then(() => { if (w === worker) w.postMessage(msg); });
  }
  function recycleIfDue() {
    if (generations < recycleAfter || pending.size > 0) return;
    worker.terminate();
    start();
  }
  // The worker is busy past a caller's patience: end it and start a fresh one. Every request the old worker had is
  // answered `replaced` (the caller decides whether to ask again). A call that was still waiting for the old worker to
  // become ready was never posted, so it is not in `pending`: the old worker's `ready` is settled here, which wakes
  // that call; it then sees a different worker and waits for the new one (the loop in call()).
  function replace() {
    const old = worker;
    old.terminate();
    for (const [id, cb] of [...pending]) { pending.delete(id); cb.resolve(failed('replaced', id)); }
    start();
    old.onceReady();
  }
  start();
  return { call, tell, recycleIfDue, replace, whenReady: () => ready, countGeneration: () => { generations++; } };
}
